import { useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Copy, ListPlus, X } from 'lucide-react';
import './selection-actions.css';

/** One small, source-bound action surface for both document viewers. */
export function SelectionActions({
  x,
  y,
  text,
  fields,
  expanded = false,
  className,
  disabled,
  onCopy,
  onAssign,
  onClose,
}: {
  x: number;
  y: number;
  text: string;
  fields: { id: string; name: string }[];
  expanded?: boolean;
  className: string;
  disabled?: boolean;
  onCopy: () => void;
  onAssign: (id: string) => void;
  onClose: () => void;
}) {
  const [choosing, setChoosing] = useState(expanded);
  const [position, setPosition] = useState({ left: 8, top: 8 });
  const root = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const element = root.current;
    if (!element) return;
    const place = () => {
      const { width, height } = element.getBoundingClientRect();
      setPosition({
        left: Math.max(8, Math.min(x + 8, innerWidth - width - 8)),
        top: Math.max(
          8,
          Math.min(
            y + height + 16 <= innerHeight ? y + 10 : y - height - 10,
            innerHeight - height - 8,
          ),
        ),
      });
    };
    place();
    const observer = new ResizeObserver(place);
    observer.observe(element);
    window.addEventListener('resize', place);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', place);
    };
  }, [x, y]);
  return createPortal(
    <div
      ref={root}
      className={`selection-actions ${className}`}
      style={position}
      role="dialog"
      data-telemetry-dialog="none"
      aria-label="Selected text actions"
      onMouseDown={(event) => {
        if (!(event.target instanceof HTMLInputElement)) event.preventDefault();
      }}
      onKeyDown={(event) => {
        event.stopPropagation();
        if (event.key === 'Escape') {
          event.preventDefault();
          onClose();
        }
        if (
          event.key === 'ArrowDown' &&
          event.target instanceof Element &&
          event.target.closest('.selection-actions-toolbar')
        ) {
          event.preventDefault();
          root.current
            ?.querySelector<HTMLButtonElement>('.selection-actions-fields button:not(:disabled)')
            ?.focus();
        }
        if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'c') {
          event.preventDefault();
          onCopy();
        }
      }}
    >
      <div className="selection-actions-toolbar">
        <button type="button" onClick={onCopy} aria-label="Copy selected text">
          <Copy size={15} />
          Copy
        </button>
        {fields.length > 0 && (
          <button
            type="button"
            aria-label="Assign to field"
            aria-expanded={choosing}
            onClick={() => {
              setChoosing((current) => !current);
              if (!choosing)
                requestAnimationFrame(() =>
                  root.current
                    ?.querySelector<HTMLButtonElement>(
                      '.selection-actions-fields button:not(:disabled)',
                    )
                    ?.focus(),
                );
            }}
          >
            <ListPlus size={15} />
            Assign to field
          </button>
        )}
        <button
          type="button"
          className="selection-actions-close"
          onClick={onClose}
          aria-label="Close selected text actions"
        >
          <X size={15} />
        </button>
      </div>
      {choosing && (
        <div className="selection-actions-picker">
          <p className="selection-actions-preview" title={text}>
            {text}
          </p>
          <div className="selection-actions-fields">
            {fields.length ? (
              fields.map((field) => (
                <button
                  key={field.id}
                  type="button"
                  disabled={disabled}
                  onClick={() => onAssign(field.id)}
                  aria-label={`Assign to ${field.name}`}
                >
                  {field.name}
                </button>
              ))
            ) : (
              <p>No fields available</p>
            )}
          </div>
        </div>
      )}
    </div>,
    window.document.body,
  );
}
